// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

// Styles
import { styled, useTheme } from "@mui/material/styles";
import {
  getSurfaceTokens,
  getPopoverPaperSx,
  getMenuItemStateColors,
} from "src/styles/mui/muiStylesHelper";

// MUI's `styled` (unlike plain `@emotion/styled`) types and injects the
// current theme into this callback automatically, so this stays in sync
// with light/dark mode without needing `useTheme()` in the component below.
// `inputHeight` is forwarded as a custom prop (filtered out of the DOM via
// shouldForwardProp) so individual call sites can override the default
// height without affecting every other SelectDropdown in the app.
const BootstrapInput = styled(Select, {
  shouldForwardProp: (prop) => prop !== "inputHeight",
})<{ inputHeight?: string }>(({ theme, inputHeight }) => {
  const { inputBackground, inputBorder, onSurface } = getSurfaceTokens(theme);
  const height = inputHeight ?? "33px";

  return {
    "& .MuiInputBase-input": {
      height: `${height} !important`,
      minHeight: height,
      borderRadius: "4px",
      position: "relative",
      border: `1px solid ${inputBorder}`,
      fontSize: "14px",
      padding: "0px 16px",
      backgroundColor: inputBackground,
      color: onSurface,
      boxShadow: "none",
      display: "flex",
      alignItems: "center",
    },
    // Select defaults to the outlined variant, which renders its own
    // fieldset/notchedOutline in addition to the border above -- without
    // this override, MUI's default (translucent, mode-dependent) outline
    // color shows through alongside the intentional one here, making this
    // control's border read slightly brighter than other input fields that
    // do set this.
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${inputBorder}`,
    },
  };
});

type SelectDropdownType = {
  id?: string;
  labelId?: string;
  required?: boolean;
  disabled?: boolean;
  dropdownOptions: Array<{
    displayName: string;
    value: string;
    disabled?: boolean;
  }>;
  value: string;
  handleChange: (event: SelectChangeEvent<any>, child: React.ReactNode) => void;
  // Overrides the input's default 33px height for this instance only.
  height?: string;
};

const SelectDropdown = ({
  id,
  labelId,
  required = false,
  disabled = false,
  dropdownOptions,
  value,
  handleChange,
  height,
}: SelectDropdownType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { inputBorder } = getSurfaceTokens(theme);
  const { selected: selectedItemColor, hover: hoverItemColor } =
    getMenuItemStateColors(theme);

  return (
    <Select
      id={id ?? "search-input-dropdown"}
      labelId={labelId}
      color="warning"
      placeholder={t("Select a value")}
      disabled={disabled}
      required={required}
      value={value}
      onChange={handleChange}
      input={<BootstrapInput inputHeight={height} />}
      fullWidth
      displayEmpty
      renderValue={(selected) => {
        if (!selected) {
          return (
            <span
              style={{
                color: inputBorder,
                width: "100%",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {t("Select a value")}
            </span>
          );
        }
        return (
          <span
            style={{
              width: "100%",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {selected}
          </span>
        );
      }}
      MenuProps={{
        MenuListProps: {
          sx: { maxHeight: "200px", overflow: "auto" },
        },
        PaperProps: {
          sx: {
            marginTop: "8px",
            maxHeight: "200px",
            overflow: "hidden",
            ...getPopoverPaperSx(theme),

            "& .MuiMenuItem-root": {
              minHeight: "36px",
            },
            "& .MuiMenuItem-root.Mui-selected": {
              backgroundColor: `${selectedItemColor} !important`,
            },
            "& .MuiMenuItem-root:hover": {
              backgroundColor: `${hoverItemColor} !important`,
            },
            "& .MuiMenuItem-root.Mui-selected:hover": {
              backgroundColor: `${selectedItemColor} !important`,
            },
          },
        },
      }}
    >
      {dropdownOptions?.length > 0 ? (
        dropdownOptions.map((option, index) => (
          <MenuItem
            key={index}
            value={option.displayName}
            disabled={option.disabled}
            sx={{ fontSize: "14px" }}
          >
            {option.displayName}
          </MenuItem>
        ))
      ) : (
        <MenuItem sx={{ pointerEvents: "none", fontSize: "14px" }}>
          {t("No results found.")}
        </MenuItem>
      )}
    </Select>
  );
};

export default SelectDropdown;
